// src/utils/statMapping.js - Map league scoring categories to player stat fields
import { formatBattingAvg, formatERA } from '../services/statFormatUtils';

/**
 * Maps scoring category labels (from league settings) to actual player stat field names
 * CANONICAL STRUCTURE: Stats are nested under hitting/pitching (matches database)
 */
export const STAT_FIELD_MAP = {
  // Hitting stats - CANONICAL: hitting.{field} (matches postgres DB structure)
  'G': { field: 'hitting.games_played', label: 'G', decimals: 0, category: 'hitting' },
  'AB': { field: 'hitting.at_bats', label: 'AB', decimals: 0, category: 'hitting' },
  'R': { field: 'hitting.runs', label: 'R', decimals: 0, category: 'hitting' },
  'H': { field: 'hitting.hits', label: 'H', decimals: 0, category: 'hitting' },
  'HR': { field: 'hitting.home_runs', label: 'HR', decimals: 0, category: 'hitting' },
  'RBI': { field: 'hitting.rbi', label: 'RBI', decimals: 0, category: 'hitting' },
  'SB': { field: 'hitting.stolen_bases', label: 'SB', decimals: 0, category: 'hitting' },
  'AVG': { field: 'hitting.batting_avg', label: 'AVG', decimals: 3, prefix: '.', category: 'hitting' },
  'OPS': { field: 'hitting.ops', label: 'OPS', decimals: 3, prefix: '.', category: 'hitting' },
  'OBP': { field: 'hitting.obp', label: 'OBP', decimals: 3, prefix: '.', category: 'hitting' },
  'SLG': { field: 'hitting.slg', label: 'SLG', decimals: 3, prefix: '.', category: 'hitting' },
  '2B': { field: 'hitting.doubles', label: '2B', decimals: 0, category: 'hitting' },
  '3B': { field: 'hitting.triples', label: '3B', decimals: 0, category: 'hitting' },
  'BB': { field: 'hitting.walks', label: 'BB', decimals: 0, category: 'hitting' },
  'K': { field: 'hitting.strikeouts', label: 'K', decimals: 0, category: 'hitting' },
  'CS': { field: 'hitting.caught_stealing', label: 'CS', decimals: 0, category: 'hitting' },
  
  // Pitching stats - CANONICAL: pitching.{field}
  'W': { field: 'pitching.wins', label: 'W', decimals: 0, category: 'pitching' },
  'SV': { field: 'pitching.saves', label: 'SV', decimals: 0, category: 'pitching' },
  'ERA': { field: 'pitching.era', label: 'ERA', decimals: 2, category: 'pitching' },
  'WHIP': { field: 'pitching.whip', label: 'WHIP', decimals: 2, category: 'pitching' },
  'SO': { field: 'pitching.strikeouts_pitched', label: 'K', decimals: 0, category: 'pitching' },
  'QS': { field: 'pitching.quality_starts', label: 'QS', decimals: 0, category: 'pitching' },
  'GS': { field: 'pitching.games_started', label: 'GS', decimals: 0, category: 'pitching' },
  'IP': { field: 'pitching.innings_pitched', label: 'IP', decimals: 1, category: 'pitching' },
  'L': { field: 'pitching.losses', label: 'L', decimals: 0, category: 'pitching' },
  'HLD': { field: 'pitching.holds', label: 'HLD', decimals: 0, category: 'pitching' },
  'BS': { field: 'pitching.blown_saves', label: 'BS', decimals: 0, category: 'pitching' },
  'ER': { field: 'pitching.earned_runs', label: 'ER', decimals: 0, category: 'pitching' },
  'R_P': { field: 'pitching.runs_allowed', label: 'R', decimals: 0, category: 'pitching' }
};

/**
 * Default stats to show when no league context (global watch list)
 */
export const DEFAULT_BATTING_STATS = ['G', 'AB', 'H', 'R', 'SB', 'HR', 'RBI', 'AVG', 'OPS'];
export const DEFAULT_PITCHING_STATS = ['GS', 'G', 'W', 'SO', 'SV', 'ERA', 'WHIP', 'QS'];

/**
 * BB for pitchers maps to walks_allowed, not walks
 * CANONICAL: pitching.walks_allowed
 */
const PITCHING_OVERRIDES = {
  'G': { field: 'pitching.games_played', label: 'G', decimals: 0, category: 'pitching' },
  'BB': { field: 'pitching.walks_allowed', label: 'BB', decimals: 0, category: 'pitching' }
};

/**
 * Get the stat configurations for a given set of stat labels
 * @param {Array<string>} statLabels - Array of stat labels from league settings
 * @param {boolean} isPitcher - Whether these are pitching stats
 * @returns {Array<Object>} Array of stat config objects
 */
export const getStatConfigs = (statLabels, isPitcher = false) => {
  if (!statLabels || !Array.isArray(statLabels)) return [];
  
  return statLabels
    .map(label => {
      // Use pitching override if applicable
      if (isPitcher && PITCHING_OVERRIDES[label]) {
        return PITCHING_OVERRIDES[label];
      }
      return STAT_FIELD_MAP[label];
    })
    .filter(config => config !== undefined);
};

/**
 * Format a stat value for display
 * @param {number|null} value - The stat value
 * @param {Object} config - The stat configuration
 * @returns {string} Formatted stat value
 */
export const formatStatValue = (value, config) => {
  if (value == null || typeof value !== 'number') {
    // Return default based on decimals
    if (config.decimals === 0) return '0';
    if (config.decimals === 3) return config.prefix ? '.000' : '0.000';
    if (config.decimals === 1) return '0.0';
    return '0.00';
  }

  // Format with appropriate decimals using shared utilities
  if (config.decimals === 3 && config.prefix) {
    // Batting average style: .249 instead of 0.249
    return formatBattingAvg(value);
  }

  if (config.decimals === 2) {
    return formatERA(value);
  }

  return value.toFixed(config.decimals);
};

/**
 * Get stat value from player object
 * CANONICAL STRUCTURE: player.stats.season.hitting.{field} or player.stats.season.pitching.{field}
 * @param {Object} player - Player object with stats
 * @param {Object} config - Stat configuration
 * @returns {number|null} The stat value
 */
export const getStatValue = (player, configOrAbbrev, statWindow = 'season') => {
  // Handle both config object and string abbreviation
  const config = typeof configOrAbbrev === 'string' 
    ? STAT_FIELD_MAP[configOrAbbrev] 
    : configOrAbbrev;
  
  if (!config) return null;
  
  // CANONICAL: Walk the nested path e.g. "hitting.home_runs" → player.stats.season.hitting.home_runs
  const parts = config.field.split('.');
  let value = player?.stats?.[statWindow];

  for (const part of parts) {
    if (!value) return null;
    value = value[part];
  }

  return value !== undefined ? value : null;
};

/**
 * Get the display label for a stat abbreviation
 * @param {string} statAbbrev - Stat abbreviation (e.g., 'R', 'HR', 'ERA')
 * @returns {string} The display label
 */
export const getStatLabel = (statAbbrev) => {
  const config = STAT_FIELD_MAP[statAbbrev];
  return config?.label || statAbbrev;
};
